import { groupInFlight } from './lib/hub-digest.js';
import {
  isRunFinished,
  type AnalysisGroup,
  type AnalysisQueue,
  type AnalysisRun,
  type GroupExecutionList,
  type TranscriptionStatus,
} from './model.js';

/**
 * The module's refresh pattern: polling, on a conditional interval.
 *
 * Three screens need movement — the queue, the dataset and the groups — and this is
 * what they use. Refetching on focus is switched off on purpose everywhere.
 *
 * ## Why polling, and what stopped being true
 *
 * It used to be the only mechanism. The queue screen is now **live**: it opens a
 * socket.io connection to Virtual Profiler with a ticket the back-office mints, and
 * its events invalidate this query — see `queue/use-queue-realtime.ts`. So for that
 * one screen polling is no longer how the news arrives; it is the net under it, for
 * the minutes when the socket is down and reconnecting.
 *
 * The events are **query invalidations and never a source of data**. That is the
 * load-bearing half: a lost event costs latency, not correctness, because this read
 * model reaches the screen by exactly one path — `GET /virtual-profiler/analysis-runs/queue`.
 *
 * The dataset and the groups have no connection of their own and still poll as the
 * mechanism.
 *
 * ## What makes it cheap
 *
 * - The interval only exists while something can still change.
 * - It stops on its own when everything has finished. No screen has to remember to
 *   turn it off, which is the part a screen always forgets.
 * - It is paused while the tab is hidden, by vue-query itself: the interval only
 *   fires when `refetchIntervalInBackground` is true or the document is visible.
 *   Left as a default it would be an accident; the queries state it.
 */

/**
 * How often the queue asks again while analyses are moving.
 *
 * A minute, and it used to be ten seconds. The socket is what makes the screen feel
 * immediate now, so this stopped being the mechanism and became the safety net: it is
 * what closes the gap while a connection is down, and while one is up it is six times
 * less traffic for the same screen.
 *
 * It is deliberately **slower than the history's interval**, which is the opposite of
 * how it started. That is not an oversight — the queue has a second, faster way to
 * find out, and the history has none.
 */
export const QUEUE_REFETCH_MS = 60_000;

/**
 * How often a page of the history asks again.
 *
 * Thirty seconds: the history is read, not watched, and what changes on it is a run
 * finishing. Unchanged when the queue's interval slowed down, which is why it is now
 * the faster of the two — polling is all this table has, so it cannot afford to be the
 * net. The socket's events do not invalidate it: a page of rows is keyed by what was
 * asked for, and refetching every open page on every event is how a live screen turns
 * into a load generator.
 */
export const HISTORY_REFETCH_MS = 30_000;

/**
 * How often the video gallery asks again while anything is moving.
 *
 * Between the two: an upload and an analysis both take minutes, and a page of cards with
 * thumbnails is more to send than the queue's one answer but less than a full table.
 */
export const DATASET_REFETCH_MS = 15_000;

/**
 * How often an open review asks whether a run of its video started, while none is in flight.
 *
 * A minute: the review has no socket, and a launch made from another screen or by somebody else
 * would otherwise never reach it — the live runs stop being asked the moment nothing is moving.
 * Slower than the gallery's interval, because here it only has to notice that something started;
 * once it has, the gallery's interval takes over until the run lands.
 */
export const REVIEW_IDLE_REFETCH_MS = 60_000;

/**
 * How often a video's transcription is asked again while its job is on its way.
 *
 * Ten seconds: a transcription takes about a minute, and the panel waiting for it is somebody
 * about to label against it. One small document per open video, and only while it moves.
 */
export const TRANSCRIPTION_REFETCH_MS = 10_000;

/**
 * How often to ask again, or `false` to stop asking.
 *
 * `false` and not zero: vue-query reads zero as "as fast as possible", so getting
 * this wrong turns a finished screen into a request loop. `idleMs` is for the one screen that has
 * to keep asking, slowly, while nothing is alive.
 */
export function refetchEvery(
  everyMs: number,
  whileAlive: boolean,
  idleMs: number | false = false,
): number | false {
  return whileAlive ? everyMs : idleMs;
}

/**
 * Whether the queue still has anything that can change by itself.
 *
 * A **paused** queue with runs waiting counts as alive. Nothing will move on its
 * own, but another operator can resume it, and the slot counters and the health
 * strip keep changing regardless — a screen that stopped asking would show a stale
 * alarm, which is the one thing an alarm must never do.
 *
 * A dead bridge counts too, so the screen notices it coming back. That is precisely
 * the moment somebody is staring at it waiting for exactly that.
 */
export function queueIsAlive(queue: AnalysisQueue | undefined): boolean {
  if (!queue) return false;

  return (
    queue.waiting.length > 0 ||
    queue.running.length > 0 ||
    !queue.health.bridgeAlive ||
    // No live worker view. The dispatcher will not dispatch in this state, so the
    // queue looks stuck; asking again is how the screen finds out it is not.
    queue.slots.source === 'unknown'
  );
}

/** Whether any run on this page of the history can still change. */
export function runsAreAlive(runs: readonly AnalysisRun[] | undefined): boolean {
  return (runs ?? []).some((run) => !isRunFinished(run.status));
}

/**
 * Whether a group of this page still has something that can move by itself: runs in flight, or an
 * execution waiting its turn. The second, because between one execution ending and the next one's
 * runs being created nothing moves, and a list that stopped asking there would never see it start.
 */
export function groupsAreAlive(
  groups: readonly Pick<AnalysisGroup, 'runsByStatus' | 'executionsWaiting'>[] | undefined,
): boolean {
  return (groups ?? []).some((group) => groupInFlight(group) > 0 || group.executionsWaiting > 0);
}

/**
 * Whether a group's executions can still change: one has not finished — an automatic retry
 * counts — or one waits its turn, which starts by itself once the one before it ends.
 */
export function groupExecutionsAreAlive(
  list: Pick<GroupExecutionList, 'items' | 'waiting'> | undefined,
): boolean {
  if (!list) return false;

  return list.waiting.length > 0 || list.items.some((batch) => !batch.complete);
}

/**
 * Whether a video's transcription can still change by itself: its job is waiting or running. Done,
 * failed and «never asked for» only change when somebody asks, and the answer to that is written
 * straight into the cache.
 */
export function transcriptionIsAlive(status: TranscriptionStatus | null | undefined): boolean {
  return status === 'pending' || status === 'running';
}
